// src/lib/agentLog.js
// Append-only admin audit log — stored in Supabase (localStorage fallback).

import { t, getLanguage } from './i18n.js'
// Reuse the single shared client so logs are written by the *authenticated*
// session (one client instance) — required once RLS is enabled.
import { supabase } from '../db/index.js'
import { chunkIds } from './coalesce.js'

const USE_SUPABASE = !!supabase

const LS_KEY      = 'sva_agent_log_v2'
const LS_CAND_LOG = 'sva_cand_log_v1'
const LS_ADMIN    = 'sva_current_admin'
const PAGE_SIZE   = 200

// ─── Admin identity ───────────────────────────────────────────────────────────
export function setCurrentAdmin(name) {
  localStorage.setItem(LS_ADMIN, name || 'Admin')
}
export function getCurrentAdmin() {
  return localStorage.getItem(LS_ADMIN) || 'Admin'
}

// ⚠️ `by` ABOVE IS A TYPED LABEL, NOT AN IDENTITY. It is whatever the user once entered, and
// on 2026-08-29 the live values included both 'gigi' and 'Gigi' for one person, plus 'Admin'
// for anybody who never set it. Nothing can be joined to it, so no per-person statistic can
// be built on it — which is why every log write now also carries the ACTOR'S ID.
//
// Set once, from App.jsx, when the session resolves the caller's agent record. Held in a
// module variable rather than threaded through forty call sites: logEvent/logCandidateEvent
// are called from everywhere, and a parameter every caller must remember is a parameter some
// caller will forget. Null until the session loads, and null is recorded honestly as
// "not attributable" rather than guessed.
let _actor = { id: null, officeId: null }
export function setLogActor(agent) {
  _actor = { id: agent?.id || null, officeId: agent?.office_id || null }
}
export function getLogActor() { return _actor }

// ─── Agent audit log ──────────────────────────────────────────────────────────
export async function logEvent(agentId, agentName, category, message) {
  const entry = {
    agent_id:   agentId,
    agent_name: agentName,
    category,
    message,
    by:         getCurrentAdmin(),
    // 0006 — the joinable half. by_office_id is the actor's office AT THE MOMENT THEY ACTED,
    // which with the dated office_manager table answers "who was the manager then" without
    // that answer changing when somebody transfers later.
    by_agent_id:  _actor.id,
    by_office_id: _actor.officeId,
    ts:         new Date().toISOString(),
  }
  if (USE_SUPABASE) {
    await supabase.from('agent_logs').insert(entry)
  } else {
    const all = _lsRead(LS_KEY)
    if (!all[agentId]) all[agentId] = []
    all[agentId].unshift({ id: crypto.randomUUID(), ...entry })
    _lsWrite(LS_KEY, all)
  }
}

export async function getLogs(agentId) {
  if (USE_SUPABASE) {
    const { data } = await supabase.from('agent_logs')
      .select('*').eq('agent_id', agentId)
      .order('ts', { ascending: false }).limit(PAGE_SIZE)
    return data || []
  }
  return _lsRead(LS_KEY)[agentId] || []
}

export async function getAllLogs({ category = '', offset = 0 } = {}) {
  if (USE_SUPABASE) {
    let q = supabase.from('agent_logs').select('*', { count: 'exact' })
      .order('ts', { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1)
    if (category) q = q.eq('category', category)
    const { data, count } = await q
    return { entries: data || [], total: count || 0 }
  }
  let entries = Object.values(_lsRead(LS_KEY)).flat()
    .sort((a, b) => b.ts.localeCompare(a.ts))
  if (category) entries = entries.filter(e => e.category === category)
  return { entries: entries.slice(offset, offset + PAGE_SIZE), total: entries.length }
}

// ─── Candidate timeline log ───────────────────────────────────────────────────
export async function logCandidateEvent(candidateId, type, message) {
  const entry = {
    candidate_id: candidateId,
    type,
    message,
    by_agent_id:  _actor.id,
    by_office_id: _actor.officeId,
    by:  getCurrentAdmin(),
    ts:  new Date().toISOString(),
  }
  if (USE_SUPABASE) {
    await supabase.from('candidate_logs').insert(entry)
  } else {
    const all = _lsRead(LS_CAND_LOG)
    if (!all[candidateId]) all[candidateId] = []
    all[candidateId].push({ id: crypto.randomUUID(), ...entry })
    _lsWrite(LS_CAND_LOG, all)
  }
}

// ─── Candidate logs: one query per TICK, not one per card ─────────────────────
// `CandidateTimeline` mounts once per candidate card and asks for its own log, so
// opening the Candidates tab issued 43 near-identical `candidate_logs` requests —
// measured on the live Desk 2026-08-09: 43 calls, 6.4 s of aggregate request time,
// to render three lines each.
//
// The component keeps asking per candidate (nothing above it has to know about
// logs); the asks are collected for one microtask and issued as a single
// `in.(…)` query. The public signature is unchanged, so every existing caller —
// including the single-candidate modal at CandidatesTab.jsx:931 — is unaffected.
//
// ⚠️ Chunked (see chunkIds): an unchunked in.() over a full roster is the shape
// that failed SILENTLY to an empty list in B567 — here a chunk that throws rejects
// its own ids and nothing else.
let logQueue = null   // { ids:Set, waiters:Map<id, {resolve,reject}[]> }

function flushCandidateLogQueue() {
  const batch = logQueue
  logQueue = null
  const ids = [...batch.ids]
  const settle = (id, rows, err) => {
    for (const w of batch.waiters.get(id) || []) err ? w.reject(err) : w.resolve(rows)
  }
  for (const chunk of chunkIds(ids)) {
    const want = PAGE_SIZE * chunk.length
    supabase.from('candidate_logs')
      // 🚨 `count: exact` is the TRUNCATION GUARD, not a statistic, and it is the
      // reason this batch is safe to share a limit. One query for many candidates
      // can be capped two ways — by the limit asked for, and by PostgREST's own
      // server-side max-rows, which is invisible from here and can sit far below it.
      // Either way the rows are dropped from the END of the ts sort, so the LAST
      // candidates would render an empty history with no error: the exact silent
      // shape of B567. Comparing the true count against what arrived detects both.
      .select('*', { count: 'exact' }).in('candidate_id', chunk)
      .order('ts', { ascending: true })
      .limit(want)
      .then(({ data, error, count }) => {
        if (error) { for (const id of chunk) settle(id, null, error); return }
        const rows = data || []
        if (count != null && count > rows.length) {
          // Truncated. Correctness beats the round trip: fall back to the old
          // per-candidate reads for THIS chunk only. Slow is recoverable; a card
          // silently missing its history is not.
          for (const id of chunk) {
            supabase.from('candidate_logs')
              .select('*').eq('candidate_id', id)
              .order('ts', { ascending: true }).limit(PAGE_SIZE)
              .then(({ data: d, error: e }) => settle(id, e ? null : (d || []), e || null),
                    err => settle(id, null, err))
          }
          return
        }
        const byId = new Map(chunk.map(id => [id, []]))
        for (const row of rows) (byId.get(row.candidate_id) || []).push(row)
        for (const id of chunk) settle(id, byId.get(id) || [])
      }, err => { for (const id of chunk) settle(id, null, err) })
  }
}

export async function getCandidateLogs(candidateId) {
  if (USE_SUPABASE) {
    if (!logQueue) {
      logQueue = { ids: new Set(), waiters: new Map() }
      queueMicrotask(flushCandidateLogQueue)
    }
    const q = logQueue
    q.ids.add(candidateId)
    return new Promise((resolve, reject) => {
      if (!q.waiters.has(candidateId)) q.waiters.set(candidateId, [])
      q.waiters.get(candidateId).push({ resolve, reject })
    }).catch(() => [])   // a failed log read must not blank the card it decorates
  }
  return _lsRead(LS_CAND_LOG)[candidateId] || []
}

// ─── Diff helper ──────────────────────────────────────────────────────────────
export function diffLog(prev, next, fields) {
  const changes = []
  fields.forEach(({ key, label }) => {
    // Only diff fields actually part of this update — partial updates (e.g. a
    // quick mark-as-left) omit most fields and must not log them as cleared.
    if (!next || !(key in next)) return
    const a = prev?.[key], b = next?.[key]
    if (a !== b) changes.push(`${label}: "${a || '—'}" → "${b || '—'}"`)
  })
  return changes.join('; ')
}

// ─── Category metadata ────────────────────────────────────────────────────────
const dateLocale = () => getLanguage() === 'ka' ? 'ka-GE' : 'en-GB'

export const LOG_CATEGORIES = {
  hired:      { get label() { return t('cat_hired')      }, icon: '🟢', color: 'text-emerald-600 dark:text-emerald-400' },
  left:       { get label() { return t('cat_left')       }, icon: '🔴', color: 'text-red-500'                           },
  schedule:   { get label() { return t('cat_schedule')   }, icon: '📅', color: 'text-blue-500'                          },
  attendance: { get label() { return t('cat_attendance') }, icon: '🕐', color: 'text-amber-500'                         },
  alert:      { get label() { return t('cat_alert')      }, icon: '⚠️',  color: 'text-orange-500'                       },
  profile:    { get label() { return t('cat_profile')    }, icon: '✏️',  color: 'text-violet-500'                       },
  office:     { get label() { return t('cat_office')     }, icon: '🏢', color: 'text-cyan-600 dark:text-cyan-400'       },
  contact:    { get label() { return t('cat_contact')    }, icon: '📞', color: 'text-teal-600 dark:text-teal-400'       },
  info:       { get label() { return t('cat_info')       }, icon: '📝', color: 'text-muted-foreground'                  },
  // 2026-09-08. Registered so the admin log's filter can actually reach these rows: an
  // unregistered category falls back to `info` in the renderer AND is missing from the
  // dropdown, so the crash reports would be written and then be unfindable — which is the
  // same "it wrote nothing / I cannot see it" failure the reporting exists to end.
  client_error: { get label() { return t('cat_client_error') }, icon: '💥', color: 'text-red-500' },
}

export const CAND_EVENT_TYPES = {
  added:       { get label() { return t('cand_ev_added')       }, icon: '➕', color: 'text-muted-foreground'                 },
  scheduled:   { get label() { return t('cand_ev_scheduled')   }, icon: '📅', color: 'text-violet-600 dark:text-violet-400' },
  rescheduled: { get label() { return t('cand_ev_rescheduled') }, icon: '🔄', color: 'text-amber-600 dark:text-amber-400'   },
  cancelled:   { get label() { return t('cand_ev_cancelled')   }, icon: '❌', color: 'text-red-500'                         },
  no_show:     { get label() { return t('cand_ev_no_show')     }, icon: '🚫', color: 'text-orange-600 dark:text-orange-400' },
  interviewed: { get label() { return t('cand_ev_interviewed') }, icon: '✅', color: 'text-blue-600 dark:text-blue-400'     },
  hired:       { get label() { return t('cand_ev_hired')       }, icon: '🟢', color: 'text-emerald-600 dark:text-emerald-400'},
  rejected:    { get label() { return t('cand_ev_rejected')    }, icon: '🔴', color: 'text-red-500'                         },
  note:        { get label() { return t('cand_ev_note')        }, icon: '📝', color: 'text-muted-foreground'                },
  // OWNER 2026-09-19 ("make it also show if seen"): opening the card is recorded, so the Desk's
  // new-applicant banner can tell a seen application from one nobody has looked at. A view is
  // NOT a touch — it lives here, never on `candidates`, so it does not clear the banner row.
  viewed:      { get label() { return t('cand_ev_viewed')      }, icon: '👁', color: 'text-muted-foreground'                },
}

// The latest view per candidate, for the new-applicant banner: { [candidate_id]: { by, ts } }.
export async function getCandidateViews(ids) {
  const out = {}
  if (!ids?.length) return out
  if (USE_SUPABASE) {
    const { data, error } = await supabase.from('candidate_logs')
      .select('candidate_id,by,ts').eq('type', 'viewed').in('candidate_id', ids)
      .order('ts', { ascending: false }).limit(500)
    if (error) throw error
    for (const r of data || []) if (!out[r.candidate_id]) out[r.candidate_id] = { by: r.by, ts: r.ts }
  } else {
    const all = _lsRead(LS_CAND_LOG)
    for (const id of ids) {
      const v = (all[id] || []).filter(e => e.type === 'viewed').sort((a, b) => String(b.ts).localeCompare(String(a.ts)))[0]
      if (v) out[id] = { by: v.by, ts: v.ts }
    }
  }
  return out
}

// ─── Client crash reporting (2026-09-08) ──────────────────────────────────────
// Owner: "on this terms view sometimes it says ჩატვირთვა ვერ მოხერხდა… this is what agents
// complain time to time on reshare page." That screen is an error BOUNDARY — a JavaScript
// exception thrown during render — and until now nothing recorded it anywhere: the only
// trace was a console.error inside the agent's own browser, which nobody reads. So the
// defect has been reported for weeks and has never once been diagnosable.
//
// Three corpus runs (893 terms forms, 170 lineage modals including 89 as the representing
// agent, and every one of those again with every core endpoint failing) produced ZERO
// crashes, so it is not the data shapes those cover. Guessing further is worth less than
// making the next occurrence name itself.
//
// ⚠️ THIS RUNS WHILE THE APP IS ALREADY BROKEN, so it may never throw, never await anything
// the caller needs, and never depend on state the crash may have corrupted. Everything is
// wrapped and the promise is deliberately floated.
//
// 📌 `build.sourcemap` is true in vite.config.js, so the minified frames below map back to
// real files and lines. If sourcemaps are ever turned off, this report loses most of its
// value — that setting and this function are a pair.
//
// Stored in `agent_logs` rather than a new table: that write path already works for every
// authenticated user (RLS `authenticated_all`), and a new table would need its own policy —
// where a misconfiguration writes nothing and looks exactly like "no crashes happened".
// `agent_id` is a TEXT sentinel (no FK), so these rows never appear in any agent's timeline;
// they are found with category `client_error`.
export const CLIENT_ERROR_AGENT_ID = '__client_error__'
export function logClientError(where, error, info) {
  try {
    const stack = String(error?.stack || '').split('\n').slice(0, 8).join(' ⏎ ')
    const comp = String(info?.componentStack || '').trim().split('\n').slice(0, 8).map(s => s.trim()).join(' ⏎ ')
    const msg = [
      `where=${where}`,
      `err=${error?.name || 'Error'}: ${String(error?.message || error)}`,
      `url=${(typeof location !== 'undefined' && location.href) || '?'}`,
      `ua=${(typeof navigator !== 'undefined' && navigator.userAgent) || '?'}`,
      `stack=${stack}`,
      `components=${comp}`,
    ].join('\n').slice(0, 6000)
    const entry = {
      agent_id: CLIENT_ERROR_AGENT_ID,
      agent_name: 'client',
      category: 'client_error',
      message: msg,
      by: getCurrentAdmin(),
      by_agent_id: _actor.id,
      by_office_id: _actor.officeId,
      ts: new Date().toISOString(),
    }
    if (USE_SUPABASE) supabase.from('agent_logs').insert(entry).then(() => {}, () => {})
    else { const all = _lsRead(LS_KEY); (all[CLIENT_ERROR_AGENT_ID] ||= []).unshift(entry); _lsWrite(LS_KEY, all) }
  } catch { /* a reporter that throws turns one broken tab into a broken page */ }
}

export function formatLogDate(isoStr) {
  const d = new Date(isoStr)
  return d.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
    + '  ' + d.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit', hour12: false })
}

// ─── localStorage helpers ─────────────────────────────────────────────────────
function _lsRead(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}') } catch { return {} }
}
function _lsWrite(key, data) {
  localStorage.setItem(key, JSON.stringify(data))
}
